"use client";

import {type ReactNode, useMemo} from "react";
import {useTranslation} from "react-i18next";
import GailIcon from "@/components/common/GailIcon";
import Icon, {ICON_TYPE} from "@/components/library/Icon";
import {ComboboxGroup} from "@/components/ui/combobox";
import {useModelCatalog} from "@/hooks/useModelCatalog";
import {AUTO_ASSISTANT_MODEL} from "@/types/ai";
import type {ModelCatalogModel, ModelCatalogProvider} from "@/types/modelCatalog";
import {canUseAnthropicModels} from "@/utils/ai/assistantToolAvailability";
import {isAutoAssistantModel} from "@/utils/ai/modelCatalog";

function catalogAutoModel(models: ModelCatalogModel[]): ModelCatalogModel | undefined {
   return models.find((model) => isAutoAssistantModel(model.alias));
}

const GOOGLE_ICON = <Icon icon="google" type={ICON_TYPE.BRAND} />;
const ANTHROPIC_ICON = <Icon icon="custom-anthropic" type={ICON_TYPE.BRAND} />;
const OPENAI_ICON = <Icon icon="openai" type={ICON_TYPE.BRAND} />;
const AUTO_ICON = <GailIcon width={12} height={12} className="size-3 shrink-0" />;

const PROVIDER_GROUPS: {provider: ModelCatalogProvider; label: string; icon: ReactNode}[] = [
   {provider: "google", label: "Google", icon: GOOGLE_ICON},
   {provider: "anthropic", label: "Anthropic", icon: ANTHROPIC_ICON},
   {provider: "openai", label: "OpenAI", icon: OPENAI_ICON},
];

const normalizeCurrentModels = (currentModels?: string | string[]): string[] => {
   if (!currentModels) return [];
   return Array.isArray(currentModels) ? currentModels : [currentModels];
};

const getModelsCacheKey = (currentModels?: string | string[]): string => {
   if (!currentModels) return "";
   if (typeof currentModels === "string") return currentModels;
   return JSON.stringify(currentModels);
};

const isPickerModel = (model: ModelCatalogModel, currentModels: string[]): boolean =>
   (model.isVisible && !model.isDeprecated) || currentModels.includes(model.alias);

export type ModelOptionsGroupedOptions = {
   allowAuto?: boolean;
};

export function useModelOptionsGrouped(
   currentModels?: string | string[],
   options?: ModelOptionsGroupedOptions
): ComboboxGroup[] {
   const {t} = useTranslation("common");
   const {models} = useModelCatalog();
   const modelsCacheKey = getModelsCacheKey(currentModels);
   const allowAuto = options?.allowAuto !== false;

   return useMemo(() => {
      const normalizedModels = normalizeCurrentModels(currentModels);
      const includeAuto = allowAuto;
      const catalogAuto = catalogAutoModel(models);

      const getModelLabel = (model: ModelCatalogModel): string => {
         const displayName = model.displayName;
         return model.isDeprecated ? `${displayName} (${t("deprecated")})` : displayName;
      };

      const autoOption = {
         value: AUTO_ASSISTANT_MODEL,
         label: catalogAuto ? getModelLabel(catalogAuto) : t("auto"),
         icon: AUTO_ICON,
      };

      if (models.length === 0) {
         return includeAuto ? [{label: "", options: [autoOption]}] : [];
      }

      const pickerModels = models.filter((model) => isPickerModel(model, normalizedModels));

      const groups: ComboboxGroup[] = [];
      if (includeAuto) {
         groups.push({
            label: "",
            options: [autoOption],
         });
      }

      for (const group of PROVIDER_GROUPS) {
         if (group.provider === "anthropic" && !canUseAnthropicModels()) {
            continue;
         }

         const options = pickerModels
            .filter((model) => model.provider === group.provider && !isAutoAssistantModel(model.alias))
            .sort((a, b) => a.sortOrder - b.sortOrder || a.alias.localeCompare(b.alias))
            .map((model) => ({
               value: model.alias,
               label: getModelLabel(model),
               icon: group.icon,
            }));

         if (options.length === 0) {
            continue;
         }

         groups.push({
            label: group.label,
            options,
         });
      }

      return groups;
   }, [allowAuto, models, modelsCacheKey, t]);
}
